import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

import { useOrganization, useOrganizationList, useUser } from "@clerk/nextjs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { STEP_INDICES } from "@/components/layouts/ReboltOnboardingLayout";
import { clientSideTrack } from "@/lib/segment/segment-client";
import { SEGMENT_EVENTS } from "@/lib/segment/segment-events";
import { completeOnboardingSchema } from "@/lib/zod-schemas/onboarding";

import type { UseFormReturn } from "react-hook-form";
import type { StepIdName } from "@/components/layouts/ReboltOnboardingLayout";
import type {
  CompleteOnboardingFormData,
  OnboardingSteps,
  useOnboardingNavigationProps,
  useOnboardingNavigationReturn,
} from "@/types/onboarding";

// ============================================================================
// Constants
// ============================================================================

const FORM_STORAGE_KEY = "onboarding-form";

// ============================================================================
// Dev Mode (development only — activated via ?devMode=true)
// ============================================================================

function isDevMode(): boolean {
  if (process.env.NODE_ENV !== "development") return false;
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("devMode") === "true";
}

const DEV_SEED_DATA: Partial<CompleteOnboardingFormData> = {
  companyName: "1354 Landscaping & Sod",
  city: "Atlanta",
  state: "GA",
  location: "Atlanta, GA",
  industry: "Landscaping",
  websiteUrl: "https://1354landscaping.com",
  userProvidedTeamMemberCount: 5,
  reboltGoals: ["get-more-leads", "manage-reviews"],
  reboltWebsiteId: "dev-test-website-id",
  reboltOrganizationId: "dev-test-org-id",
};

// ============================================================================
// Session Storage Helpers
// ============================================================================

function getStoredFormData(): Partial<CompleteOnboardingFormData> | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(FORM_STORAGE_KEY);
    if (stored) return JSON.parse(stored);

    if (isDevMode()) return DEV_SEED_DATA;

    return null;
  } catch {
    return null;
  }
}

function storeFormData(data: CompleteOnboardingFormData): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
}

export function clearStoredFormData(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(FORM_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

// ============================================================================
// Onboarding Session Flag
// ============================================================================

let onboardingSessionActive = false;

export function markOnboardingSessionActive() {
  onboardingSessionActive = true;
}

// ============================================================================
// Form Hook
// ============================================================================

const DEFAULT_FORM_VALUES: CompleteOnboardingFormData = {
  companyName: "",
  location: "",
  city: "",
  state: "",
  serpApiData: [],
  companyPlaceId: undefined,
  industry: "",
  websiteUrl: "",
  userProvidedTeamMemberCount: 1,
  annualRevenue: undefined,
  yearsInBusiness: undefined,
  reboltGoals: [],
  otherGoals: "",
  reboltOrganizationId: undefined,
  reboltWebsiteId: undefined,
};

function useOnboardingForm() {
  const getInitialFormValues = (): CompleteOnboardingFormData => {
    const stored = getStoredFormData();
    return stored ? { ...DEFAULT_FORM_VALUES, ...stored } : DEFAULT_FORM_VALUES;
  };

  const form = useForm<CompleteOnboardingFormData>({
    resolver: zodResolver(completeOnboardingSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: getInitialFormValues(),
  });

  // persist form data to sessionStorage on changes
  useEffect(() => {
    const subscription = form.watch((data) => {
      storeFormData(data as CompleteOnboardingFormData);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const resetForm = () => {
    form.reset(DEFAULT_FORM_VALUES);
    clearStoredFormData();
  };

  return { form, resetForm, defaultFormValues: DEFAULT_FORM_VALUES };
}

// ============================================================================
// Step Access Control Hook
// ============================================================================

interface UseStepAccessControlParams {
  steps: OnboardingSteps;
  form: UseFormReturn<CompleteOnboardingFormData>;
  isSignedIn: boolean | undefined;
  hasOrganization: boolean;
  devMode: boolean;
}

function useStepAccessControl({ steps, form, isSignedIn, hasOrganization, devMode }: UseStepAccessControlParams) {
  /**
   * Determines if a step is accessible based on auth state and onboarding progress.
   *
   * Gating rules:
   * 1. Bounds check: step must be within valid range (0 to steps.length - 1)
   * 2. Auth gating:
   *    - Signed-in users can't access account creation step
   *    - Guests can't access any step past account creation step
   * 3. Location data gating (users are able to go to company creation step without location data if they manually enter URL):
   *    - Can't skip past company search without city/state data
   * 4. Business creation gating:
   *    - Can't skip ahead to post-creation steps without a business
   *    - Can't go back to pre-creation steps once business exists
   *
   * This prevents:
   * - URL manipulation to skip steps
   * - Browser back button going to invalid states
   * - Creating duplicate companies by going back
   */
  const isStepAccessible = (stepIndex: number): boolean => {
    if (stepIndex < STEP_INDICES.CREATE_REBOLT_ACCOUNT || stepIndex >= steps.length) return false;

    // auth gating: signed-in users can't go back to sign-up step
    if (isSignedIn && stepIndex === STEP_INDICES.CREATE_REBOLT_ACCOUNT) return false;

    // auth gating: guests can't proceed past sign-up step
    if (!isSignedIn && stepIndex > STEP_INDICES.CREATE_REBOLT_ACCOUNT) return false;

    // dev mode: skip business/location gating so all steps are freely navigable
    if (devMode) return true;

    // check if business was created
    const hasBusinessBeenCreated = Boolean(form.getValues("reboltWebsiteId")) || hasOrganization;

    const hasLocationData = Boolean(form.getValues("city") && form.getValues("state"));

    if (!hasLocationData && stepIndex > STEP_INDICES.SEARCH_USER_BUSINESS) {
      return false;
    }

    // company creation gating: can't skip ahead past company creation if no company exists
    if (!hasBusinessBeenCreated && stepIndex > STEP_INDICES.CREATE_REBOLT_BUSINESS) {
      return false;
    }

    // company creation gating: once company is created, can't go back to pre-creation steps
    if (hasBusinessBeenCreated && stepIndex <= STEP_INDICES.CREATE_REBOLT_BUSINESS) {
      return false;
    }

    return true;
  };

  const getDefaultStep = (): number => {
    return isSignedIn ? STEP_INDICES.SEARCH_USER_BUSINESS : STEP_INDICES.CREATE_REBOLT_ACCOUNT;
  };

  return { isStepAccessible, getDefaultStep };
}

// ============================================================================
// URL Sync Hook
// ============================================================================

interface UseStepUrlSyncParams {
  steps: OnboardingSteps;
  form: UseFormReturn<CompleteOnboardingFormData>;
  currentStepIndex: number | null;
  setCurrentStepIndex: (index: number) => void;
  isStepAccessible: (stepIndex: number) => boolean;
  getDefaultStep: () => number;
  isInitialized: boolean;
  setIsInitialized: (value: boolean) => void;
  shouldRedirect: boolean;
  isLoaded: boolean;
}

function useStepUrlSync({
  steps,
  form,
  currentStepIndex,
  setCurrentStepIndex,
  isStepAccessible,
  getDefaultStep,
  isInitialized,
  setIsInitialized,
  shouldRedirect,
  isLoaded,
}: UseStepUrlSyncParams) {
  const router = useRouter();
  const { step: stepFromUrl } = router.query;
  const isUpdatingUrl = useRef(false);

  const resolveStepFromUrl = (): number => {
    if (typeof stepFromUrl === "string") {
      const stepIndex = steps.findIndex((s) => s.id === stepFromUrl);
      if (stepIndex !== -1 && isStepAccessible(stepIndex)) {
        return stepIndex;
      }
    }
    return getDefaultStep();
  };

  const updateUrlWithStep = (stepIndex: number, replace = false) => {
    const stepId = steps[stepIndex]?.id;
    if (!stepId) return;

    isUpdatingUrl.current = true;

    // include websiteId from form state if available
    const formWebsiteId = form.getValues("reboltWebsiteId");
    const newQuery: Record<string, string> = { ...router.query, step: stepId };
    if (formWebsiteId) {
      newQuery.websiteId = formWebsiteId;
    }

    const method = replace ? router.replace : router.push;
    method({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true }).finally(() => {
      isUpdatingUrl.current = false;
    });
  };

  // initialize step from URL on first load
  useEffect(() => {
    if (!isLoaded || shouldRedirect || !router.isReady) return;

    if (!isInitialized) {
      const initialStep = resolveStepFromUrl();
      setCurrentStepIndex(initialStep);
      setIsInitialized(true);

      // ensure URL reflects the actual step (in case we fell back to default)
      const urlStep = typeof stepFromUrl === "string" ? stepFromUrl : null;
      const actualStepId = steps[initialStep]?.id;
      if (urlStep !== actualStepId) {
        updateUrlWithStep(initialStep, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, shouldRedirect, router.isReady, isInitialized, stepFromUrl, steps]);

  // sync state when URL changes (browser back/forward)
  useEffect(() => {
    if (!isInitialized || isUpdatingUrl.current || !router.isReady) return;

    if (typeof stepFromUrl === "string") {
      const stepIndex = steps.findIndex((s) => s.id === stepFromUrl);
      if (stepIndex !== -1 && isStepAccessible(stepIndex) && stepIndex !== currentStepIndex) {
        setCurrentStepIndex(stepIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepFromUrl, isInitialized, router.isReady, steps, currentStepIndex]);

  return { updateUrlWithStep };
}

// ============================================================================
// Auth Redirect Hook
// ============================================================================

function useAuthRedirect() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const { userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const router = useRouter();
  const { websiteId } = router.query;

  const membershipsReady = !isSignedIn || (userMemberships && !userMemberships.isLoading);

  useEffect(() => {
    if (!isLoaded || !isOrgLoaded || !membershipsReady) return;

    if (organization && !onboardingSessionActive) {
      setShouldRedirect(true);
      window.location.href = `${websiteId ? `/dashboard/sites/${websiteId}/home` : "/dashboard"}`;
      return;
    }

    // user has existing orgs but none active — send to org selection
    if (
      isSignedIn &&
      !organization &&
      !onboardingSessionActive &&
      userMemberships?.data &&
      userMemberships.data.length > 0
    ) {
      setShouldRedirect(true);
      window.location.href = "/dashboard/select-organization";
    }
  }, [isLoaded, isOrgLoaded, membershipsReady, organization, isSignedIn, userMemberships?.data, websiteId]);

  return {
    isLoaded: isLoaded && membershipsReady,
    isOrgLoaded,
    isSignedIn,
    user,
    shouldRedirect,
    hasOrganization: Boolean(organization),
  };
}

// ============================================================================
// Main Navigation Hook
// ============================================================================

export function useOnboardingNavigation({ steps }: useOnboardingNavigationProps): useOnboardingNavigationReturn {
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const devMode = isDevMode();

  // prevent dashboard redirect when testing the flow in dev mode
  if (devMode) {
    markOnboardingSessionActive();
  }

  // auth & redirect
  const { isLoaded, isSignedIn, user, shouldRedirect, isOrgLoaded, hasOrganization } = useAuthRedirect();

  // form management
  const { form, resetForm } = useOnboardingForm();

  // step access control
  const { isStepAccessible, getDefaultStep } = useStepAccessControl({
    steps,
    form,
    isSignedIn,
    hasOrganization,
    devMode,
  });

  // URL synchronization
  const { updateUrlWithStep } = useStepUrlSync({
    steps,
    form,
    currentStepIndex,
    setCurrentStepIndex,
    isStepAccessible,
    getDefaultStep,
    isInitialized,
    setIsInitialized,
    shouldRedirect,
    isLoaded,
  });

  useEffect(() => {
    if (devMode) {
      console.info(
        "%c[Onboarding Dev Mode] All steps unlocked. Form pre-seeded with test data.",
        "color: #f59e0b; font-weight: bold"
      );
    }
  }, [devMode]);

  // handle auth state changes (user signs in while on step 0)
  useEffect(() => {
    if (!isInitialized || !isLoaded) return;

    if (isSignedIn && currentStepIndex === STEP_INDICES.CREATE_REBOLT_ACCOUNT) {
      setCurrentStepIndex(STEP_INDICES.SEARCH_USER_BUSINESS);
      updateUrlWithStep(STEP_INDICES.SEARCH_USER_BUSINESS, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentStepIndex, isInitialized, isLoaded]);

  // ---- Navigation Actions ----

  const goToStep = (stepIndex: number, options?: { replace?: boolean }) => {
    if (!isStepAccessible(stepIndex)) return;
    setCurrentStepIndex(stepIndex);
    updateUrlWithStep(stepIndex, options?.replace);
  };

  const goToNamedStep = (stepName: StepIdName, options?: { replace?: boolean }) => {
    const stepIndex = steps.findIndex((step) => step.id === stepName);
    if (stepIndex !== -1) {
      goToStep(stepIndex, options);
    }
  };

  const previousStep = () => {
    if (currentStepIndex === null) return;
    const minStep = isSignedIn ? STEP_INDICES.SEARCH_USER_BUSINESS : STEP_INDICES.CREATE_REBOLT_ACCOUNT;
    if (currentStepIndex > minStep) {
      goToStep(currentStepIndex - 1);
    }
  };

  const nextStep = async () => {
    if (currentStepIndex === null) return;
    if (!isSignedIn && currentStepIndex === STEP_INDICES.CREATE_REBOLT_ACCOUNT) return;

    const currentStep = steps[currentStepIndex];

    clientSideTrack({
      event: SEGMENT_EVENTS.CRM_ONBOARDING_STEP_COMPLETE,
      userId: user?.id ?? null,
      properties: {
        step_name: currentStep.id,
        stepIndex: currentStepIndex,
        formData: form.getValues(),
      },
    });

    if (currentStepIndex < steps.length - 1) {
      goToStep(currentStepIndex + 1);
    }
  };

  const reset = () => {
    resetForm();
    const defaultStep = getDefaultStep();
    setCurrentStepIndex(defaultStep);
    updateUrlWithStep(defaultStep, true);
    setIsSubmitting(false);
  };

  // ---- Return ----

  const safeCurrentStepIndex = currentStepIndex ?? 0;
  const isAuthLoaded = isLoaded && isOrgLoaded && isInitialized && currentStepIndex !== null && !shouldRedirect;

  return {
    currentStepIndex: safeCurrentStepIndex,
    isAuthLoaded,
    isSubmitting,
    form,
    goToStep,
    goToNamedStep,
    nextStep,
    previousStep,
    reset,
    devMode,
  };
}
